import { useCallback, useMemo, useRef, useState } from 'react';

import _ from 'lodash';

import { TEAM_CHAT_MENTION_DEBOUNCE_MS } from 'constants/teamChat';
import useDebounce from 'hooks/useDebounce';

import { filterMentionCandidates, getMemberId, getMentionQueryAtCaret } from '../../TeamChat.utils';

const useChatMentions = ({ members, currentUserId, priorityMemberIds }) => {
  const [mentionRange, setMentionRange] = useState(null);
  const [mentionQuery, setMentionQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [focusedMemberId, setFocusedMemberId] = useState(null);

  const setDebouncedQueryValue = useDebounce(value => {
    setDebouncedQuery(value);
  }, TEAM_CHAT_MENTION_DEBOUNCE_MS);

  const suggestions = useMemo(() => {
    if (_.isNil(mentionRange)) {
      return [];
    }

    return filterMentionCandidates({
      members,
      currentUserId,
      priorityMemberIds,
      query: debouncedQuery,
    });
  }, [members, currentUserId, priorityMemberIds, debouncedQuery, mentionRange]);

  const prevSuggestionsRef = useRef(suggestions);

  if (prevSuggestionsRef.current !== suggestions) {
    prevSuggestionsRef.current = suggestions;

    if (!suggestions.length && !_.isNil(focusedMemberId)) {
      setFocusedMemberId(null);
    }

    if (suggestions.length) {
      const stillVisible = suggestions.some(member => getMemberId(member) === focusedMemberId);

      if (!stillVisible) {
        setFocusedMemberId(getMemberId(suggestions[0]));
      }
    }
  }

  const updateFromCaret = useCallback(
    (value, caret) => {
      const range = getMentionQueryAtCaret(value, caret);

      setMentionRange(range);
      setMentionQuery(range?.query ?? '');
      setDebouncedQueryValue(range?.query ?? '');
    },
    [setDebouncedQueryValue],
  );

  const reset = useCallback(() => {
    setMentionRange(null);
    setMentionQuery('');
    setDebouncedQuery('');
  }, []);

  return {
    mentionRange,
    mentionQuery,
    suggestions,
    focusedMemberId,
    setFocusedMemberId,
    updateFromCaret,
    reset,
  };
};

export default useChatMentions;
